import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { LOGIN_PAGES } from '../login-process/enum-login-pages';

import { DialogService } from 'primeng/dynamicdialog';
import { LoginModalComponent } from 'src/app/components/login-modal/login-modal.component';
import { LoginService } from 'src/app/services/login-service/login.service';
import { Router } from '@angular/router';
import { ConfigService } from 'src/app/services/config-service/config.service';
import { MixpanelService } from '../../services/mixpanel-service/mixpanel.service';
import { Subscription } from 'rxjs';
import { DeviceService } from '../../services/device-service/device.service';
import { AuthService } from '@auth0/auth0-angular';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  fleetFinderURL = '';
  deviceType = '';
  private deviceTypeSubscription: Subscription | undefined;
  loading = false;

  constructor(
    public dialogService: DialogService,
    private loginService: LoginService,
    private router: Router,
    private configService: ConfigService,
    private deviceService: DeviceService,
    private mixpanelService: MixpanelService,
    private renderer: Renderer2,
    public auth: AuthService,
    @Inject(DOCUMENT) private doc: Document
  ) {
    this.subscribeToConfig();
  }

  ngOnInit(): void {
    this.subscribeToAuth();
    this.checkSessionState();
    this.subscribeToDeviceType();
  }

  ngAfterViewInit(): void {
    this.setupBackgroundVideo();
  }

  ngOnDestroy(): void {
    if (this.deviceTypeSubscription) {
      this.deviceTypeSubscription.unsubscribe();
    }
  }

  loginWithRedirect(): void {
    this.auth.loginWithRedirect();
  }

  isUserLogin(): boolean {
    const userEmail = localStorage.getItem('userEmail') || '';
    return userEmail !== null && userEmail !== '';
  }

  goToFaq(): void {
    this.router.navigate(['/faqs']);
  }

  goToFleetFinder(): void {
    window.location.href = this.fleetFinderURL;
  }

  private subscribeToConfig(): void {
    this.configService._config_$.subscribe((configUrls) => {
      this.fleetFinderURL = configUrls.fleetFinderURL;
    });
  }

  private subscribeToAuth(): void {
    this.auth.isAuthenticated$.subscribe((loggedIn) => {
      if (loggedIn && this.loginService.isValidUser()) {
        this.showLoginModal();
      }
    });
  }

  private checkSessionState(): void {
    this.auth.isAuthenticated$.subscribe((loggedIn) => {
      if (loggedIn) {
        this.loading = true;
        this.loginService.getUserEmail()?.subscribe({
          next: (email: string) => {
            this.loading = false;
            localStorage.setItem('userEmail', email);
          },
          error: () => {
            this.loading = false;
          },
        });
      }
    });
  }

  private subscribeToDeviceType(): void {
    this.deviceTypeSubscription = this.deviceService
      .getDeviceTypeObservable()
      .subscribe((type) => {
        this.deviceType = type;
      });
  }

  private trackInitialLoadEvent(email?: string): void {
    this.mixpanelService.track({
      id: email,
      eventName: 'Initial Get Started',
      eventDisplayName: 'Get Started Button Click',
      kpi: 'Click-Through Rate',
      trigger: 'On Button Click',
      eventDefinition: 'User clicks the get started button',
      propertyType: 'Event Property',
      propertyDefinition: 'Button showLoginModal',
    });
  }

  public showLoginModal(from?: string): void {
    if (this.loginService.isSessionValid()) {
      this.handleValidSession();
      return;
    }
    this.trackInitialLoadEvent();
    if (from === 'waitlist') {
      this.dialogService.open(LoginModalComponent, {
        data: {
          step: LOGIN_PAGES.SIGN_UP_SCREEN,
        },
        width: '980px',
        closeOnEscape: false,
      });
    }
  }

  private handleValidSession(): void {
    this.loading = true;
    this.loginService.getUserEmail()?.subscribe({
      next: (email: string) => {
        this.trackInitialLoadEvent(email);
        this.loading = false;
        if (email) {
          localStorage.setItem('userEmail', email);
          this.router.navigate(['/launch']);
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private setupBackgroundVideo(): void {
    const video: HTMLVideoElement = document.getElementById(
      'background-video'
    ) as HTMLVideoElement;

    const playVideo = () => {
      video.muted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;

      video.play().catch((error) => {
        console.log(
          'Autoplay was prevented. Trying to play video on user interaction.',
          error
        );
      });
    };

    const addUserInteractionListeners = () => {
      const interactionEvents = ['click', 'touchstart', 'scroll', 'keydown'];
      interactionEvents.forEach((event) => {
        this.renderer.listen('document', event, () => {
          playVideo();
        });
      });
    };

    playVideo();
    addUserInteractionListeners();
  }
}
